import React from 'react';

const AnimationControls = ({ config, onChange }) => {
  const handleChange = (key, value) => {
    onChange({ [key]: value });
  };

  const animationTypes = [
    { value: 'fadeIn', label: 'Fade In' },
    { value: 'slideIn', label: 'Slide In' },
    { value: 'bounce', label: 'Bounce' },
    { value: 'shimmer', label: 'Shimmer' },
    { value: 'scale', label: 'Scale' },
    { value: 'staggered', label: 'Staggered List' },
  ];

  const curves = [
    { value: 'easeInOut', label: 'Ease In Out' },
    { value: 'easeIn', label: 'Ease In' },
    { value: 'easeOut', label: 'Ease Out' },
    { value: 'linear', label: 'Linear' },
    { value: 'spring', label: 'Spring' },
  ];

  const directions = [
    { value: 'up', label: 'Up' },
    { value: 'down', label: 'Down' },
    { value: 'left', label: 'Left' },
    { value: 'right', label: 'Right' },
  ];

  return (
    <div className="space-y-6">
      {/* Animation Type */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Animation Type
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {animationTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => handleChange('type', type.value)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${
                  config.type === type.value
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {type.label}
            </button>
          ))}
        </div>
      </div>

      {/* Duration */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Duration: {config.duration}s
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(0.1 - 3.0s)</span>
        </label>
        <input
          type="range"
          min="0.1"
          max="3"
          step="0.1"
          value={config.duration || 0.5}
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            if (value >= 0.1 && value <= 3) {
              handleChange('duration', value);
            }
          }}
          className="w-full"
        />
      </div>

      {/* Delay */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Delay: {config.delay}s
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(0 - 2.0s)</span>
        </label>
        <input
          type="range"
          min="0"
          max="2"
          step="0.1"
          value={config.delay || 0}
          onChange={(e) => {
            const value = parseFloat(e.target.value);
            if (value >= 0 && value <= 2) {
              handleChange('delay', value);
            }
          }}
          className="w-full"
        />
      </div>

      {/* Curve */}
      {config.type !== 'staggered' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Animation Curve
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
            {curves.map((curve) => (
              <button
                key={curve.value}
                onClick={() => handleChange('curve', curve.value)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium transition-all
                  ${
                    config.curve === curve.value
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {curve.label}
              </button>
            ))}
          </div>
          
          {/* Spring Parameters */}
          {config.curve === 'spring' && (
            <div className="mt-4 space-y-4 p-4 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Spring Response: {config.springResponse}s
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(0.1 - 2.0s)</span>
                </label>
                <input
                  type="range"
                  min="0.1"
                  max="2.0"
                  step="0.1"
                  value={config.springResponse || 0.5}
                  onChange={(e) => handleChange('springResponse', parseFloat(e.target.value))}
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  Damping Fraction: {config.springDampingFraction}
                  <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(0.0 - 1.0)</span>
                </label>
                <input
                  type="range"
                  min="0.0"
                  max="1.0"
                  step="0.05"
                  value={config.springDampingFraction || 0.6}
                  onChange={(e) => handleChange('springDampingFraction', parseFloat(e.target.value))}
                  className="w-full"
                />
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                  Lower values = more bouncy, higher values = less bouncy
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Direction (for slideIn) */}
      {(config.type === 'slideIn' || config.type === 'staggered') && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Direction
          </label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {directions.map((dir) => (
              <button
                key={dir.value}
                onClick={() => handleChange('direction', dir.value)}
                className={`
                  px-3 py-2 rounded-lg text-xs font-medium transition-all
                  ${
                    config.direction === dir.value
                      ? 'bg-gradient-primary text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                  }
                `}
              >
                {dir.label}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Stagger (for staggered) */}
      {config.type === 'staggered' && (
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Stagger Amount: {config.stagger}s
            <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">(0.05 - 0.5s)</span>
          </label>
          <input
            type="range"
            min="0.05"
            max="0.5"
            step="0.05"
            value={config.stagger || 0.1}
            onChange={(e) => {
              const value = parseFloat(e.target.value);
              if (value >= 0.05 && value <= 0.5) {
                handleChange('stagger', value);
              }
            }}
            className="w-full"
          />
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
            Delay between each item in the list
          </p>
        </div>
      )}

      {/* Repeat Options */}
      {config.type !== 'shimmer' && config.type !== 'staggered' && (
        <div className="p-4 bg-gray-50 dark:bg-gray-800/50 rounded-lg">
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={config.repeat || false}
              onChange={(e) => handleChange('repeat', e.target.checked)}
              className="rounded"
            />
            Repeat Animation
          </label>
          {config.repeat && (
            <div className="mt-3">
              <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">
                Repeat Count (0 = infinite): {config.repeatCount || 1}
              </label>
              <input
                type="range"
                min="0"
                max="10"
                value={config.repeatCount || 1}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (value >= 0 && value <= 10) {
                    handleChange('repeatCount', value);
                  }
                }}
                className="w-full"
              />
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {config.repeatCount === 0 ? 'Animation will repeat infinitely' : `Animation will repeat ${config.repeatCount} times`}
              </p>
            </div>
          )}
        </div>
      )}

      {/* Apply To */}
      <div>
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Apply To
        </label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {['view', 'list', 'grid'].map((apply) => (
            <button
              key={apply}
              onClick={() => handleChange('applyTo', apply)}
              className={`
                px-4 py-2 rounded-lg text-sm font-medium transition-all
                ${
                  config.applyTo === apply
                    ? 'bg-gradient-primary text-white'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-300 dark:hover:bg-gray-600'
                }
              `}
            >
              {apply.charAt(0).toUpperCase() + apply.slice(1)}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default AnimationControls;

